import React, {
  lazy,
  Suspense,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  BrowserRouter,
  Link,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import './App.css';
import HomePage from './pages/HomePage';
import SiteHeader from './components/site/SiteHeader';
import SiteFooter from './components/site/SiteFooter';
import RouteErrorBoundary from './components/site/RouteErrorBoundary';
import RouteLoadingState from './components/site/RouteLoadingState';
import { site } from './content/siteCopy';

const InsightsPage = lazy(() => import('./pages/InsightsPage'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPost = lazy(() => import('./components/BlogPost'));

function RouteNotFound() {
  return (
    <section className="route-message" aria-labelledby="not-found-title">
      <p className="route-message__eyebrow">404</p>
      <h1 id="not-found-title">This page could not be found.</h1>
      <p>The page may have moved, or the address may be incomplete.</p>
      <Link className="route-message__link" to="/">Return home</Link>
    </section>
  );
}

function RouteTransitionManager() {
  const location = useLocation();
  const previousPathname = useRef(location.pathname);

  useEffect(() => {
    if (previousPathname.current === location.pathname) {
      return undefined;
    }

    previousPathname.current = location.pathname;
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return undefined;
    }

    const animationFrame = window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      document.getElementById('main-content')?.focus({ preventScroll: true });
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [location.pathname]);

  return null;
}

function RouteContent({ initialPostId }) {
  const location = useLocation();
  const navigate = useNavigate();
  const encodedPostId = initialPostId
    ? encodeURIComponent(initialPostId)
    : null;
  const isGeneratedPostDocument = encodedPostId && (
    location.pathname === `/posts/${encodedPostId}`
    || location.pathname === `/posts/${encodedPostId}.html`
  );

  const route = isGeneratedPostDocument ? (
    <BlogPost
      postId={initialPostId}
      onBack={() => navigate('/insights')}
    />
  ) : (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/insights" element={<InsightsPage />} />
      <Route path="/posts/:postId" element={<BlogPage />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="*" element={<RouteNotFound />} />
    </Routes>
  );

  return (
    <RouteErrorBoundary
      resetKey={`${location.pathname}${location.search}`}
    >
      <Suspense fallback={<RouteLoadingState />}>
        {route}
      </Suspense>
    </RouteErrorBoundary>
  );
}

function AppShell({ initialPostId = null }) {
  const [siteName, setSiteName] = useState(site.name);

  useEffect(() => {
    if (typeof fetch !== 'function') {
      return undefined;
    }

    fetch('/manifest.json')
      .then(response => {
        if (!response.ok) {
          throw new Error(`Manifest request failed with status ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        if (data.short_name) {
          setSiteName(data.short_name);
        }
      })
      .catch(error => console.error('Error fetching manifest:', error));

    return undefined;
  }, []);

  return (
    <div className="App">
      <RouteTransitionManager />
      <SiteHeader siteName={siteName} />
      <main id="main-content" className="site-main" tabIndex={-1}>
        <RouteContent initialPostId={initialPostId} />
      </main>
      <SiteFooter />
    </div>
  );
}

function readInitialPostId() {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.initialPostId || null;
}

function App() {
  const [initialPostId] = useState(readInitialPostId);

  if (typeof window === 'undefined') {
    return null;
  }

  return (
    <BrowserRouter>
      <AppShell initialPostId={initialPostId} />
    </BrowserRouter>
  );
}

export {
  AppShell,
};

export default App;
